/**
 * PATH: apps/hr/src/pages/documents/documents-list/UploadModal.tsx
 * PURPOSE: Modal for uploading documents with drag-and-drop, employee selection, and doc type
 * WHY: Complex upload UI with file validation, D&D, and custom doc type creation
 */

import { useState, useRef } from 'react'
import { FileText, Upload, X, Plus, RefreshCw } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { Employee } from '../../../types'

interface UploadModalProps {
  isOpen: boolean
  onClose: () => void
  employees: Employee[]
  onUpload: (files: File[], employeeId: string, docType: string) => Promise<void>
}

const DOC_TYPE_OPTIONS = [
  { value: '', label: 'Auto-detect' },
  { value: 'license', label: 'License' },
  { value: 'certification', label: 'Certification' },
  { value: 'contract', label: 'Contract' },
  { value: 'identification', label: 'Identification' },
  { value: 'visa', label: 'Visa' },
  { value: 'medical', label: 'Medical' },
  { value: 'training', label: 'Training' },
  { value: 'other', label: 'Other' },
]

function validateFiles(files: FileList | File[]): File[] {
  const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg']
  const maxSize = 10 * 1024 * 1024
  return Array.from(files).filter(file => {
    if (!validTypes.includes(file.type)) { alert(`${file.name} is not a supported file type`); return false }
    if (file.size > maxSize) { alert(`${file.name} exceeds 10MB limit`); return false }
    return true
  })
}

export function UploadModal({ isOpen, onClose, employees, onUpload }: UploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [selectedEmployee, setSelectedEmployee] = useState('')
  const [selectedDocType, setSelectedDocType] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [showNewDocTypeInput, setShowNewDocTypeInput] = useState(false)
  const [newDocTypeName, setNewDocTypeName] = useState('')

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setSelectedFiles(prev => [...prev, ...validateFiles(e.target.files!)])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    if (e.dataTransfer.files) setSelectedFiles(prev => [...prev, ...validateFiles(e.dataTransfer.files)])
  }

  const handleClose = () => {
    setSelectedFiles([]); setSelectedEmployee(''); setSelectedDocType('')
    setShowNewDocTypeInput(false); setNewDocTypeName(''); onClose()
  }

  const handleUpload = async () => {
    if (selectedFiles.length === 0) { alert('Please select at least one file'); return }
    if (!selectedEmployee) { alert('Please select an employee'); return }
    setIsUploading(true)
    try { await onUpload(selectedFiles, selectedEmployee, selectedDocType); handleClose() }
    catch { alert('Upload failed. Please try again.') }
    finally { setIsUploading(false) }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-auto m-4">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Upload Document</h2>
            <p className="text-sm text-slate-500">Upload a new document for AI processing</p>
          </div>
          <button onClick={handleClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg" multiple onChange={handleFileSelect} className="hidden" />
          
          <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-8 text-center cursor-pointer hover:border-teal-500 dark:hover:border-teal-500 transition-colors" onClick={() => fileInputRef.current?.click()} onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
            <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600 dark:text-slate-400 mb-2">Drag and drop your files here, or click to browse</p>
            <p className="text-sm text-slate-500 mb-4">Supported: PDF, PNG, JPG, JPEG (Max 10MB)</p>
            <button type="button" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }} className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700">
              Select Files
            </button>
          </div>

          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Selected Files ({selectedFiles.length})</p>
              <div className="max-h-32 overflow-y-auto space-y-2">
                {selectedFiles.map((file, index) => (
                  <div key={`${file.name}-${index}`} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-teal-500 flex-shrink-0" />
                      <span className="text-sm text-slate-700 dark:text-slate-300 truncate">{file.name}</span>
                      <span className="text-xs text-slate-500 flex-shrink-0">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                    </div>
                    <button type="button" onClick={() => !isUploading && setSelectedFiles(prev => prev.filter((_, i) => i !== index))} disabled={isUploading} className={cn("p-1 transition-colors", isUploading ? "text-slate-300 cursor-not-allowed" : "text-slate-400 hover:text-red-500")}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Employee <span className="text-red-500">*</span></label>
              <select value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                <option value="">Select Employee</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Document Type</label>
              {showNewDocTypeInput ? (
                <div className="flex gap-2">
                  <input type="text" value={newDocTypeName} onChange={(e) => setNewDocTypeName(e.target.value)} placeholder="Type name" className="flex-1 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" autoFocus />
                  <button type="button" onClick={() => { if (newDocTypeName.trim()) { setSelectedDocType(newDocTypeName.trim()); setShowNewDocTypeInput(false); setNewDocTypeName('') } }} className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700">Add</button>
                </div>
              ) : (
                <select value={selectedDocType} onChange={(e) => setSelectedDocType(e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                  {DOC_TYPE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              )}
              <button type="button" onClick={() => setShowNewDocTypeInput(!showNewDocTypeInput)} className="mt-2 text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 flex items-center gap-1.5 transition-colors">
                <Plus className="w-4 h-4" />
                {showNewDocTypeInput ? 'Select existing type' : 'Create New Type'}
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200 dark:border-slate-800">
          <button onClick={handleClose} disabled={isUploading} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50">Cancel</button>
          <button onClick={handleUpload} disabled={selectedFiles.length === 0 || !selectedEmployee || isUploading} className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2">
            {isUploading ? (<><RefreshCw className="w-4 h-4 animate-spin" />Uploading...</>) : (<><Upload className="w-4 h-4" />Upload &amp; Process</>)}
          </button>
        </div>
      </div>
    </div>
  )
}
